'use client';
import routes from '@config/routes';
import { apiFetcher } from '@instance';
import { trackGa4Search } from '@v2/analytics/ga4';
import { AlertTriangle } from '@v2/ui/Icon/Alert';
import { AlertToast } from '@v2/ui/toast';
import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useDebounceCallback } from 'usehooks-ts';
import { DesktopSearchInput } from './components/DesktopSearchInput/DesktopSearchInput';
import { MobileSearchInput } from './components/MobileSearchInput/MobileSearchInput';

import devConsole from '@utils/developer-console';
import {
  formatSearchResultData,
  type SearchResultsProps,
} from './services/format-search-result-data';

interface Props {
  isMobileSmallVersion?: boolean;
}

export const Search = ({ isMobileSmallVersion = false }: Props) => {
  const [isSearchInputOnFocus, setIsSearchInputOnFocus] = useState(false);
  const [isSearchMobileInputOnFocus, setIsSearchMobileInputOnFocus] =
    useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResultsProps[]>();
  const [searchValue, setSearchValue] = useState('');
  const trackedSearches = useRef(new Set<string>());
  const searchRequestId = useRef(0);

  const [occursAtFilter, setOccursAtFilter] = useState('');

  const toggleInputFocus = (newValue?: boolean) => {
    setIsSearchInputOnFocus((oldValue) => {
      if (typeof newValue !== 'undefined') {
        return newValue;
      }
      return !oldValue;
    });
  };

  const toggleMobileInputFocus = () => {
    setIsSearchMobileInputOnFocus((oldValue) => !oldValue);
  };

  const getAPIData = useCallback(async (search: string, occursAt: string) => {
    const requestId = ++searchRequestId.current;
    setIsLoadingResults(true);
    setSearchResults(undefined);
    try {
      const { results, locations, events } = await apiFetcher(routes.search, {
        body: JSON.stringify({ q: search, occursAt }),
      });

      const formattedSearchResult = formatSearchResultData(
        results,
        locations,
        events
      );

      if (requestId !== searchRequestId.current) return;

      setSearchResults(formattedSearchResult);

      const normalizedSearch = search.trim().toLowerCase();
      const searchKey = `${normalizedSearch}:${occursAt}`;
      if (!trackedSearches.current.has(searchKey)) {
        trackedSearches.current.add(searchKey);
        trackGa4Search({
          searchTerm: search,
          resultCount: formattedSearchResult.reduce(
            (total, section) => total + section.sectionItems.length,
            0
          ),
          resultTypes: formattedSearchResult.map(
            (section) => section.sectionTitle
          ),
          surface: 'global_search',
        });
      }
    } catch (e: any) {
      if (requestId !== searchRequestId.current) return;
      devConsole({ e });
      toast.custom(
        (t) => (
          <AlertToast
            {...t}
            title="Oops, something went wrong"
            content={'Please try again in a minute.'}
            icon={<AlertTriangle />}
          />
        ),
        { position: 'top-right' }
      );
    } finally {
      if (requestId === searchRequestId.current) setIsLoadingResults(false);
    }
  }, []);

  const debounceSearch = useDebounceCallback(
    (searchValue: string, occursAtFilter: string) => {
      getAPIData(searchValue, occursAtFilter);
    },
    1000
  );

  const onChangeSearchInput = useCallback(
    (value: string) => {
      searchRequestId.current += 1;
      if (!value.trimStart().length) setSearchResults([]);
      setSearchValue(value);
      setSearchResults(undefined);
      setIsLoadingResults(false);
      debounceSearch.cancel();
      if (value.trimStart().length > 2) {
        setIsLoadingResults(true);
        debounceSearch(value.trimStart(), occursAtFilter);
      }
    },
    [debounceSearch, occursAtFilter]
  );

  const onDateFilterChange = useCallback(
    async (dateFilter: string) => {
      setOccursAtFilter(dateFilter);

      if (searchValue.trimStart().length) {
        toggleInputFocus(true);
        await getAPIData(searchValue.trimStart(), dateFilter);
      }
    },
    [getAPIData, searchValue]
  );

  return (
    <>
      <div
        className={isMobileSmallVersion ? 'h-6 md:hidden' : 'mx-4 mb-6 md:mx-0'}
      >
        <DesktopSearchInput
          isSearchInputOnFocus={isSearchInputOnFocus}
          toggleInputFocus={toggleInputFocus}
          searchResults={searchResults}
          inputValue={searchValue}
          isLoadingResults={isLoadingResults}
          onChangeInput={onChangeSearchInput}
          onDateChange={onDateFilterChange}
        />
        <MobileSearchInput
          isSearchMobileInputOnFocus={isSearchMobileInputOnFocus}
          toggleMobileInputFocus={toggleMobileInputFocus}
          searchResults={searchResults}
          inputValue={searchValue}
          isLoadingResults={isLoadingResults}
          onChangeInput={onChangeSearchInput}
          isMobileSmallVersion={isMobileSmallVersion}
        />
      </div>
    </>
  );
};
